import { useParams, Navigate, Link } from "react-router-dom";
import { useEffect } from "react";

interface PodcastInfo {
  slug: string;
  name: string;
  host: string;
  description: string;
  rssUrl: string;
  officialUrl: string;
  category: string;
  frequency: string;
}

// Allowlisted podcasts only - manually vetted for legal safety
const PODCASTS: Record<string, PodcastInfo> = {
  "joe-rogan-experience": {
    slug: "joe-rogan-experience",
    name: "The Joe Rogan Experience",
    host: "Joe Rogan",
    description: "The Joe Rogan Experience podcast features long-form conversations with guests from all walks of life—comedians, actors, musicians, MMA fighters, authors, scientists, and more. Known for in-depth discussions that often run 3+ hours.",
    rssUrl: "https://anchor.fm/s/1c1c3e44/podcast/rss",
    officialUrl: "https://open.spotify.com/show/4rOoJ6Egrf8K2IrywzwOMk",
    category: "Comedy · Society & Culture",
    frequency: "Multiple episodes per week",
  },
  "lex-fridman-podcast": {
    slug: "lex-fridman-podcast",
    name: "Lex Fridman Podcast",
    host: "Lex Fridman",
    description: "Conversations about science, technology, history, philosophy, and the nature of intelligence, consciousness, love, and power. Guests include researchers, entrepreneurs, and thought leaders across many disciplines.",
    rssUrl: "https://lexfridman.com/feed/podcast/",
    officialUrl: "https://lexfridman.com/podcast/",
    category: "Science · Technology",
    frequency: "2-3 episodes per week",
  },
  "huberman-lab": {
    slug: "huberman-lab",
    name: "Huberman Lab",
    host: "Dr. Andrew Huberman",
    description: "Huberman Lab discusses neuroscience and science-based tools for everyday life. Dr. Andrew Huberman, a neuroscientist at Stanford, explains how our brain and its connections with our body control our perceptions, behaviors, and health.",
    rssUrl: "https://feeds.megaphone.fm/hubermanlab",
    officialUrl: "https://hubermanlab.com/",
    category: "Health · Science",
    frequency: "Weekly",
  },
  "all-in-podcast": {
    slug: "all-in-podcast",
    name: "All-In Podcast",
    host: "Chamath, Jason, Sacks & Friedberg",
    description: "Industry veterans, besties, and billionaires Chamath Palihapitiya, Jason Calacanis, David Sacks, and David Friedberg cover all things economic, tech, political, and social.",
    rssUrl: "https://feeds.megaphone.fm/all-in-with-chamath-jason-sacks-friedberg",
    officialUrl: "https://www.allinpodcast.co/",
    category: "Business · Technology",
    frequency: "Weekly",
  },
  "my-first-million": {
    slug: "my-first-million",
    name: "My First Million",
    host: "Sam Parr & Shaan Puri",
    description: "Sam Parr and Shaan Puri brainstorm new business ideas based on trends and opportunities they see in the market. They also interview successful entrepreneurs about how they built their companies.",
    rssUrl: "https://feeds.megaphone.fm/my-first-million",
    officialUrl: "https://www.mfmpod.com/",
    category: "Business · Entrepreneurship",
    frequency: "3-4 episodes per week",
  },
  "tim-ferriss-show": {
    slug: "tim-ferriss-show",
    name: "The Tim Ferriss Show",
    host: "Tim Ferriss",
    description: "Tim Ferriss deconstructs world-class performers from eclectic areas to extract the tactics, tools, and routines you can use. Guests include investors, chess prodigies, athletes, and more.",
    rssUrl: "https://rss.art19.com/tim-ferriss-show",
    officialUrl: "https://tim.blog/podcast/",
    category: "Business · Self-Improvement",
    frequency: "2-3 episodes per week",
  },
  "diary-of-a-ceo": {
    slug: "diary-of-a-ceo",
    name: "The Diary of a CEO",
    host: "Steven Bartlett",
    description: "The Diary of a CEO with Steven Bartlett features candid conversations with the world's most influential people in business, health, and culture. Deep dives into what makes people successful.",
    rssUrl: "https://feeds.megaphone.fm/DOAC",
    officialUrl: "https://stevenbartlett.com/the-diary-of-a-ceo-podcast/",
    category: "Business · Entrepreneurship",
    frequency: "2-3 episodes per week",
  },
  "acquired": {
    slug: "acquired",
    name: "Acquired",
    host: "Ben Gilbert & David Rosenthal",
    description: "Acquired tells the stories of great companies: the strategies and tactics that made them successful, and the lessons we can learn from them. Deep-dive episodes on tech, business, and investing history.",
    rssUrl: "https://feeds.megaphone.fm/acquired",
    officialUrl: "https://www.acquired.fm/",
    category: "Business · Technology",
    frequency: "Bi-weekly",
  },
};

// Get all podcasts for listing
export const getAllPodcasts = () => Object.values(PODCASTS);

export default function PodcastLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const podcast = slug ? PODCASTS[slug] : null;

  useEffect(() => {
    if (podcast) {
      document.title = `${podcast.name} Transcripts - Echonour`;
      
      // JSON-LD for SEO
      const jsonld = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${podcast.name} Transcripts`,
        "description": `Get accurate transcripts of ${podcast.name} episodes with speaker identification and timestamps.`,
        "url": `https://echonour.com/transcripts/${podcast.slug}`,
        "mainEntity": {
          "@type": "PodcastSeries",
          "name": podcast.name,
          "description": podcast.description,
          "url": podcast.officialUrl,
          "author": {
            "@type": "Person",
            "name": podcast.host
          }
        }
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonld);
      script.id = 'podcast-jsonld';
      document.head.appendChild(script);
      
      return () => {
        const existing = document.getElementById('podcast-jsonld');
        if (existing) existing.remove();
      };
    }
  }, [podcast]);

  if (!podcast) {
    return <Navigate to="/browse" replace />;
  }

  const transcribeUrl = `/?url=${encodeURIComponent(podcast.rssUrl)}`;

  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {/* Legal Disclaimer - Above the fold */}
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <strong>Disclaimer:</strong> Echonour is not affiliated with, endorsed by, or connected to {podcast.name} or {podcast.host}. 
          This page provides transcription services for publicly available podcast content.
          {" "}
          <a 
            href={podcast.officialUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            Visit the official {podcast.name} website →
          </a>
        </div>

        {/* Header */}
        <div className="rounded-xl border border-gray-200 bg-white p-8 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            {/* Podcast Icon (no artwork - legal safety) */}
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-3xl font-bold text-white">
              {podcast.name.charAt(0)}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span>{podcast.category}</span>
                <span>•</span>
                <span>{podcast.frequency}</span>
              </div>
              <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
                {podcast.name} Transcripts
              </h1>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Hosted by {podcast.host}
              </p>
            </div>
          </div>

          <p className="mt-6 text-gray-700 dark:text-gray-300">
            {podcast.description}
          </p>

          {/* CTA */}
          <div className="mt-8">
            <Link
              to={transcribeUrl}
              className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700"
            >
              <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              Transcribe {podcast.name} Episode
            </Link>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Paste any episode URL to get a full transcript with timestamps and speaker names
            </p>
          </div>
        </div>

        {/* What You Get */}
        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-8 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            What You Get
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div className="flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Speaker Identification</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Know who said what. {podcast.host} and guests labeled by name.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Timestamps</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Click any timestamp to jump to that moment in the audio.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">AI Summary</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get key points and topics covered without reading the full transcript.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Searchable</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Find any topic or quote instantly across all your transcripts.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-8 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            How to Transcribe {podcast.name}
          </h2>
          <ol className="mt-6 space-y-4">
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                1
              </span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Find the episode</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Copy the YouTube URL or episode link from your podcast app
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                2
              </span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Paste and transcribe</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Paste the URL into Echonour and click Transcribe
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                3
              </span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Get your transcript</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  In 2-5 minutes, your full transcript with speakers and timestamps is ready
                </p>
              </div>
            </li>
          </ol>
        </div>

        {/* CTA Bottom */}
        <div className="mt-8 rounded-xl border border-blue-200 bg-blue-50 p-8 text-center dark:border-blue-800 dark:bg-blue-900/20">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ready to transcribe {podcast.name}?
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Get 20 hours of free transcription. No credit card required.
          </p>
          <Link
            to={transcribeUrl}
            className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700"
          >
            Start Transcribing Free
            <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Other Podcasts */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Other Popular Podcasts
          </h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.values(PODCASTS)
              .filter((p) => p.slug !== podcast.slug)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/transcripts/${p.slug}`}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:border-blue-300 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-700 dark:hover:text-blue-400"
                >
                  {p.name}
                </Link>
              ))}
          </div>
        </div>

        {/* Footer Disclaimer */}
        <div className="mt-12 border-t border-gray-200 pt-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <p>
            Echonour is an independent transcription service. We are not affiliated with {podcast.name}.
          </p>
          <p className="mt-2">
            <a 
              href={podcast.officialUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              Official {podcast.name} Website
            </a>
            {" • "}
            <a href="mailto:support@echonour.com" className="underline hover:no-underline">
              DMCA / Takedown Requests
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
